import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  createMedicalHistory,
  getCampMedicalHistory,
  updateMedicalHistory,
} from '../../../services/CampManagementService'
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  TextField,
  Button,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  MEDICAL_HISTORY_ITEMS,
  MEDICAL_HISTORY_PREFIX,
  medicalHistoryMessages,
} from '../constants/Constants'

type MedicalHistoryItem = {
  id: string
  name: string
  selected: boolean
}

interface PastMedicalHistoryProps {
  patientUhid?: string
  visitId?: string
  campId?: string
  onSuccess?: () => void
  showSaveButton?: boolean
}

const PREFIX = MEDICAL_HISTORY_PREFIX

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  formLabel: `${PREFIX}-formLabel`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  sectionTitleWithMargin: `${PREFIX}-sectionTitleWithMargin`,
  gridContainer: `${PREFIX}-gridContainer`,
  notesField: `${PREFIX}-notesField`,
  submitButton: `${PREFIX}-submitButton`,
  notesContainer: `${PREFIX}-notesContainer`,
}

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    padding: theme.spacing(3),
    [`& .${classes.sectionTitle}`]: {
      margin: `${theme.spacing(2)} 0 ${theme.spacing(1)}`,
    },
    [`& .${classes.sectionTitleWithMargin}`]: {
      margin: '16px 0 8px',
    },
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
      '&.MuiButton-contained': {
        backgroundColor: '#4682b4 !important',
        color: '#f1f3f5 !important',
        fontWeight: 600,
        border: '1px solid #4682b4 !important',
      },
    },
  },
  [`& .${classes.title}`]: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#363463',
  },
  [`& .${classes.formLabel}`]: {
    '& .MuiFormControlLabel-label': {
      fontSize: '12px',
      marginLeft: '4px',
      lineHeight: '1.3',
    },
    '& .MuiFormControlLabel-root': {
      marginRight: '6px',
    },
    '& .MuiCheckbox-root': {
      padding: '3px 5px',
      '& svg': {
        width: '18px',
        height: '18px',
      },
    },
  },
  [`& .${classes.gridContainer}`]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
    marginTop: '16px',
  },
  [`& .${classes.notesContainer}`]: {
    marginTop: '20px',
  },
  [`& .${classes.notesField}`]: {
    marginTop: '8px',
    '& .MuiInputBase-root': {
      backgroundColor: '#ffffff',
    },
  },
  [`& .${classes.submitButton}`]: {
    marginTop: '16px',
    padding: '6px 24px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    backgroundColor: '#363463',
    '&:hover': {
      backgroundColor: '#4a4a7d',
    },
  },
}))

interface AppointmentData {
  visitId?: string
  uhid?: string
  campId?: string
}

interface PastMedicalHistoryProps {
  appointmentData?: AppointmentData
  onSuccess?: () => void
}

const PastMedicalHistory = ({
  appointmentData,
  onSuccess,
  showSaveButton = true,
}: PastMedicalHistoryProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [medicalHistoryId, setMedicalHistoryId] = useState<string | null>(null)
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistoryItem[]>(
    MEDICAL_HISTORY_ITEMS?.map((item) => ({
      id: item?.toLowerCase()?.replace(/\s+/g, '-'),
      name: item,
      selected: false,
    }))
  )
  const [additionalNotes, setAdditionalNotes] = useState('')
  const [familyCancerHistories, setFamilyCancerHistories] = useState<any[]>([])
  const [previousSurgeries, setPreviousSurgeries] = useState('')
  const [drugAllergies, setDrugAllergies] = useState('')
  const [otherConditions, setOtherConditions] = useState('')

  const handleHistoryChange = (id: string) => {
    setMedicalHistory((prev) =>
      prev?.map((item) =>
        item?.id === id ? { ...item, selected: !item?.selected } : item
      )
    )
  }

  const handleAdditionalNotesChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setAdditionalNotes(e?.target?.value)
  }

  const fetchMedicalHistory = useCallback(async () => {
    if (!appointmentData?.uhid || !appointmentData?.visitId) return

    try {
      const response = await getCampMedicalHistory(appointmentData?.visitId)
      const responseData = response?.data || {}
      if (responseData) {
        const {
          id = '',
          pastMedicalHistories = [],
          familyCancerHistories = [],
          previousSurgeries = '',
          drugAllergies = '',
          otherConditions = '',
          additionalNotes: notes = '',
        } = responseData

        if (id) {
          setMedicalHistoryId(id)
        }

        // Update medical history checkboxes
        setMedicalHistory((prev) =>
          prev?.map((item) => ({
            ...item,
            selected: pastMedicalHistories?.includes(item?.name) || false,
          }))
        )

        setFamilyCancerHistories(
          Array?.isArray(familyCancerHistories) ? familyCancerHistories : []
        )
        setPreviousSurgeries(previousSurgeries)
        setDrugAllergies(drugAllergies)
        setOtherConditions(otherConditions)
        setAdditionalNotes(notes)
      }
    } catch (error) {
      toast.error(medicalHistoryMessages?.error?.fetchFailed)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    appointmentData,
    setMedicalHistoryId,
    setMedicalHistory,
    setFamilyCancerHistories,
    setPreviousSurgeries,
    setDrugAllergies,
    setOtherConditions,
    setAdditionalNotes,
    setIsSubmitting,
  ])

  // Fetch medical history data when component mounts
  useEffect(() => {
    fetchMedicalHistory()
  }, [appointmentData?.visitId, appointmentData?.uhid, fetchMedicalHistory])

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const selectedHistory = medicalHistory?.filter((item) => item?.selected)

      const payload = {
        patientUhid: appointmentData?.uhid || '',
        visitId: appointmentData?.visitId || '',
        campId: appointmentData?.campId || '',
        pastMedicalHistories: selectedHistory?.map((item) => item?.name),
        familyCancerHistories,
        previousSurgeries:
          previousSurgeries?.trim() ||
          medicalHistoryMessages.defaults.emptyField,
        drugAllergies:
          drugAllergies?.trim() || medicalHistoryMessages.defaults.emptyField,
        otherConditions:
          otherConditions?.trim() || medicalHistoryMessages.defaults.emptyField,
        additionalNotes:
          additionalNotes?.trim() || medicalHistoryMessages.defaults.emptyField,
      }

      if (medicalHistoryId) {
        await updateMedicalHistory(payload, medicalHistoryId)
        toast.success(medicalHistoryMessages.success.updated)
      } else {
        await createMedicalHistory(payload)
        toast.success(medicalHistoryMessages.success.created)
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        medicalHistoryMessages.error.saveFailed
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Root className={classes.container}>
      <Typography variant='h6' className={classes.title}>
        {medicalHistoryMessages.sections.title}
      </Typography>
      <FormGroup>
        <div className={classes.gridContainer}>
          {medicalHistory?.map((item) => (
            <div key={item?.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item?.selected}
                    onChange={() => handleHistoryChange(item?.id)}
                    color='primary'
                  />
                }
                label={item?.name}
                className={classes.formLabel}
              />
            </div>
          ))}
        </div>
      </FormGroup>

      <div className={classes.notesContainer}>
        <Typography variant='subtitle2' className={classes.sectionTitle}>
          {medicalHistoryMessages.sections.previousSurgeries}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant='outlined'
          value={previousSurgeries}
          onChange={(e) => setPreviousSurgeries(e?.target?.value)}
          placeholder={medicalHistoryMessages.placeholders.previousSurgeries}
          className={classes.notesField}
        />

        <Typography variant='subtitle2' className={classes.sectionTitle}>
          {medicalHistoryMessages.sections.drugAllergies}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant='outlined'
          value={drugAllergies}
          onChange={(e) => setDrugAllergies(e?.target?.value)}
          placeholder={medicalHistoryMessages.placeholders.drugAllergies}
          className={classes.notesField}
        />

        <Typography variant='subtitle2' className={classes.sectionTitle}>
          {medicalHistoryMessages.sections.otherConditions}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant='outlined'
          value={otherConditions}
          onChange={(e) => setOtherConditions(e?.target?.value)}
          placeholder={medicalHistoryMessages.placeholders.otherConditions}
          className={classes.notesField}
        />

        <Typography
          variant='subtitle2'
          className={classes.sectionTitleWithMargin}
        >
          {medicalHistoryMessages.sections.additionalNotes}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          variant='outlined'
          placeholder={medicalHistoryMessages.placeholders.additionalNotes}
          value={additionalNotes}
          onChange={handleAdditionalNotesChange}
          className={classes.notesField}
        />
      </div>

      {showSaveButton !== false && (
        <Button
          variant='contained'
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? medicalHistoryMessages.buttons.saving
            : medicalHistoryMessages.buttons.save}
        </Button>
      )}
    </Root>
  )
}

export default PastMedicalHistory
